import { useState } from "react";
import PropTypes from "prop-types";
import { NavLink, matchPath, useLocation } from "react-router-dom";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "@/lib/utils";

// Sidebar navigation. The legacy MUI implementation leaned on
// ListItemButton + Collapse + ListItemIcon styling; this version is
// plain Tailwind + react-router-dom NavLink, matching the rest of
// the migrated chrome and dropping the @mui/material dependency.
//
// Each `item` shape (from layouts/dashboard/NavConfig):
//   { title, path?, icon: (isActive) => ReactNode, info?, children? }
// Parent items with `children` toggle a nested submenu, mirroring
// the old Collapse behaviour without the animation primitive.

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

const ROW_CLS =
  "group flex h-12 w-full items-center rounded-md px-3 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground";

function NavItem({ item, active }) {
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  if (children) {
    return (
      <li>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className={cn(
            ROW_CLS,
            isActiveRoot && "font-medium text-primary",
          )}
          aria-expanded={open}
        >
          {/* Icons tint themselves via fill="currentColor", so they inherit
            this row's colour — muted, primary when selected, foreground on
            hover — and work in dark mode. Don't reintroduce a colour class. */}
        <span className="flex size-6 items-center justify-center">
            {typeof icon === "function" ? icon(isActiveRoot) : null}
          </span>
          <span className="ml-3 flex-1 text-left">{title}</span>
          {info ?? null}
          {open ? (
            <ChevronDownIcon className="ml-1 size-4 shrink-0" />
          ) : (
            <ChevronRightIcon className="ml-1 size-4 shrink-0" />
          )}
        </button>
        {open ? (
          <ul className="mt-1 space-y-0.5">
            {children.map((child) => {
              const isActiveSub = active(child.path);
              return (
                <li key={child.title}>
                  <NavLink
                    to={child.path}
                    className={cn(
                      ROW_CLS,
                      isActiveSub && "font-medium text-primary",
                    )}
                  >
                    {/* Icons tint themselves via fill="currentColor", so they inherit
            this row's colour — muted, primary when selected, foreground on
            hover — and work in dark mode. Don't reintroduce a colour class. */}
        <span className="flex size-6 items-center justify-center">
                      <span
                        className={cn(
                          "inline-block size-1 rounded-full transition-transform",
                          isActiveSub
                            ? "scale-[2] bg-primary"
                            : "bg-muted-foreground/60",
                        )}
                        aria-hidden
                      />
                    </span>
                    <span className="ml-3 flex-1 text-left">{child.title}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        ) : null}
      </li>
    );
  }

  return (
    <li>
      <NavLink
        to={path}
        className={cn(
          ROW_CLS,
          isActiveRoot && "font-medium text-primary",
        )}
      >
        {/* Icons tint themselves via fill="currentColor", so they inherit
            this row's colour — muted, primary when selected, foreground on
            hover — and work in dark mode. Don't reintroduce a colour class. */}
        <span className="flex size-6 items-center justify-center">
          {typeof icon === "function" ? icon(isActiveRoot) : null}
        </span>
        <span className="ml-3 flex-1 text-left">{title}</span>
        {info ?? null}
      </NavLink>
    </li>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
  className: PropTypes.string,
};

export default function NavSection({ navConfig, className }) {
  const { pathname } = useLocation();
  const match = (path) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  return (
    <nav className={className}>
      <ul className="space-y-0.5 p-2">
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={match} />
        ))}
      </ul>
    </nav>
  );
}
